import { ReactNode, useEffect, useState } from 'react';
import ObraPlay from '@/components/Icons/ObraPlay';
import { IOSSwitch } from '@/components/IOSSwitch';
import { useCommunications } from '@/contexts/Communications';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import {
  IActiveCommunications,
  ICommunicationGroup,
} from '@/types/api/communications';
import { handleErrorAsync } from '@/utils/handlers';
import {
  ArrowBack,
  EmailOutlined,
  ExpandMore,
  InfoOutlined,
  WhatsApp,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  BoxProps,
  Button,
  Card,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';

interface IConfigureNotifications {
  currentModal: string | null;
  handleClose: (value: boolean) => void;
  idNotification: number | undefined;
}

interface IOptionNotification {
  title: string;
  activeCommunication: IActiveCommunications;
}

interface IChildrenOptionNotification {
  title: SubscriptionTitle;
  active?: boolean;
  icon?: ReactNode;
  handleChangeSubscription: (title: SubscriptionTitle) => Promise<void>;
}

const SubscriptionType = {
  ['E-mail']: 'is_email_active',
  ['WhatsApp']: 'is_whatsapp_active',
  ['Sistema Obra Play']: 'is_notification_active',
} as const;

type SubscriptionTitle = keyof typeof SubscriptionType;

const OptionNotification = ({
  title,
  activeCommunication,
  ...props
}: IOptionNotification & BoxProps) => {
  const { sx } = props;
  const {
    is_whatsapp_supported,
    is_email_supported,
    is_notification_supported,
    is_whatsapp_active,
    is_email_active,
    is_notification_active,
    description,
    subscription,
    code,
  } = activeCommunication || {};

  const { updateCommunications } = useCommunications();

  const { setIsLoading } = useLoading();

  const updatedSubscription = {
    is_email_active: subscription
      ? subscription.is_email_active
      : is_email_active,
    is_notification_active: subscription
      ? subscription.is_notification_active
      : is_notification_active,
    is_whatsapp_active: subscription
      ? subscription.is_whatsapp_active
      : is_whatsapp_active,
  };

  const handleCreateSubscription = async (title: SubscriptionTitle) => {
    const currentSubscription = SubscriptionType[title];

    const [subscriptionData, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      const payload = {
        ...updatedSubscription,
        [currentSubscription]: !updatedSubscription[currentSubscription],
      };

      const { data } = await api.post('/api/communication_subscriptions/', {
        communication: code,
        ...payload,
      });

      return data;
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast('error', error.message);
      return;
    }

    updateCommunications({
      ...activeCommunication,
      subscription: subscriptionData,
    });
  };

  const handleEditSubscription = async (title: SubscriptionTitle) => {
    const currentSubscription = SubscriptionType[title];

    const [subscriptionData, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      const payload = {
        ...updatedSubscription,
        [currentSubscription]: !updatedSubscription[currentSubscription],
      };

      const { data } = await api.put(
        `/api/communication_subscriptions/${subscription?.id}`,
        {
          communication: code,
          ...payload,
        }
      );

      return data;
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast('error', error.message);
      return;
    }

    updateCommunications({
      ...activeCommunication,
      subscription: subscriptionData,
    });
  };

  return (
    <Accordion
      sx={{
        '&:not(:first-child):before': {
          opacity: '1 !important',
          display: 'block !important',
        },
        ...sx,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ ...sx }}>
        <Tooltip title={description} placement="right">
          <Box display="flex" alignItems="center">
            <Typography variant="body2">{title}</Typography>
            <IconButton
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <InfoOutlined
                sx={{ width: '18px', height: '18px' }}
                color="primary"
              />
            </IconButton>
          </Box>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          gap: 1,
          display: 'grid',
          paddingY: 0,
        }}
      >
        {is_email_supported && (
          <ChildrenOptionNotification
            title="E-mail"
            icon={
              <EmailOutlined
                sx={{ color: 'text.secondary', fontSize: '16px' }}
              />
            }
            active={updatedSubscription.is_email_active}
            handleChangeSubscription={
              subscription ? handleEditSubscription : handleCreateSubscription
            }
          />
        )}
        {is_whatsapp_supported && (
          <ChildrenOptionNotification
            title="WhatsApp"
            icon={
              <WhatsApp sx={{ color: 'text.secondary', fontSize: '16px' }} />
            }
            active={updatedSubscription.is_whatsapp_active}
            handleChangeSubscription={
              subscription ? handleEditSubscription : handleCreateSubscription
            }
          />
        )}
        {is_notification_supported && (
          <ChildrenOptionNotification
            title="Sistema Obra Play"
            icon={<ObraPlay sx={{ fontSize: '16px' }} />}
            active={updatedSubscription.is_notification_active}
            handleChangeSubscription={
              subscription ? handleEditSubscription : handleCreateSubscription
            }
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ChildrenOptionNotification = ({
  title,
  active,
  handleChangeSubscription,
  icon,
}: IChildrenOptionNotification) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <IOSSwitch
        color="primary"
        checked={active}
        onChange={() => handleChangeSubscription(title)}
      />
    </Box>
  );
};

const ConfigureNotifications = ({
  currentModal,
  handleClose,
  idNotification,
}: IConfigureNotifications) => {
  const { setIsLoading } = useLoading();
  const { setGroupCommunications, groupCommunications } = useCommunications();

  const [nameGroup, setNameGroup] = useState<string | undefined>();

  const handleResetNotification = async () => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.post(`/api/reset_communication_groups/${idNotification}`);
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    const updatedGroupCommunications = {
      ...groupCommunications,
      active_communications: groupCommunications?.active_communications?.map(
        communication => ({
          ...communication,
          subscription: communication.subscription
            ? {
                ...communication.subscription,
                is_email_active: communication.is_email_active,
                is_notification_active: communication.is_notification_active,
                is_whatsapp_active: communication.is_whatsapp_active,
              }
            : null,
        })
      ),
    };

    setGroupCommunications(updatedGroupCommunications);

    Toast('success', 'Notificações restauradas com sucesso!');
  };

  useEffect(() => {
    const handleNotificationGroups = async () => {
      const [group, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const { data } = await api.get<ICommunicationGroup>(
          `/api/communication_groups/${idNotification}`
        );

        return data;
      }).finally(() => setIsLoading(false));

      if (error) return Toast('error', error.message);

      setGroupCommunications(group);
      setNameGroup(group.name);
    };

    idNotification && handleNotificationGroups();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModal]);

  return (
    <Modal
      aria-labelledby="modal-configure-notifications"
      aria-describedby="modal-configure-notifications-description"
      sx={{ overflowY: 'auto' }}
      open={currentModal !== null}
      onClose={() => handleClose(false)}
    >
      <>
        <Box
          sx={{
            boxShadow: '0px 1px 1px 0px #0000001A',
            background: '#FFFFFF',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px 16px',
            height: '68px',
            position: 'relative',
            zIndex: 999,
          }}
        >
          <IconButton
            onClick={() => handleClose(false)}
            sx={{ color: '#000000', position: 'absolute', left: '16px' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="h2">
            {nameGroup}
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: 'background.default', minHeight: '100%' }}>
          <MainContainer
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Alert
              severity="info"
              sx={{
                width: '100%',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#EDEDED',
                color: 'text.secondary',
                '& .MuiAlert-icon': { color: 'text.secondary' },
              }}
            >
              <Typography variant="caption">
                Aviso importante: outras plataformas ainda podem enviar
                notificações de cotação e ordem de compra mesmo que desativadas
                no Obra Play.
              </Typography>
            </Alert>
            <Card sx={{ p: 2, gap: 2, display: 'grid', borderRadius: '12px' }}>
              {groupCommunications?.active_communications?.map(
                (activeCommunication: IActiveCommunications, index) => {
                  return (
                    <OptionNotification
                      key={activeCommunication.code}
                      title={activeCommunication.name}
                      activeCommunication={activeCommunication}
                      sx={{ pt: index === 0 ? 0 : '8px' }}
                    />
                  );
                }
              )}
            </Card>

            <Box
              display="flex"
              width="100%"
              justifyContent="center"
              marginY={2}
            >
              <Button
                sx={{ width: 'max-content' }}
                onClick={handleResetNotification}
              >
                restaurar notificações padrão
              </Button>
            </Box>
          </MainContainer>
        </Box>
      </>
    </Modal>
  );
};

export default ConfigureNotifications;
